import SCREENSHOT_CROPS from "./feature-screenshot-crops.json";

// Real Zobliss screenshots (from the help center) shown on each feature row.
// Key = feature slug, then tab `value` from feature-showcase-content.js.
// Screenshots listed in feature-screenshot-crops.json use a focused copy
// (no app sidebar / settings menu), made by scripts/crop-feature-screenshots.js.
export const shot = (path) =>
    SCREENSHOT_CROPS[path]
        ? `/images/features/focus/${path}.webp`
        : `/images/help-center/${path}.webp`;

// Full-screen kiosk captures (outputs/landing-images/kiosk-*.mjs, iPad landscape).
// Shown whole inside a tablet frame instead of as a cropped card.
const KIOSK_SHOTS = "/images/features/pos-kiosk/";
export const isTabletShot = (src) => Boolean(src?.startsWith(KIOSK_SHOTS));

export const FEATURE_SHOWCASE_SCREENSHOTS = {
    "booking-scheduling": {
        "live-availability": shot("calendar/calendar-week-view"),
        "smart-reminders": shot("calendar/automated-reminder-edit"),
        "team-visibility": shot("calendar/calendar-team-filter"),
        "rebooking-flow": shot("calendar/appointment-checkout"),
    },
    "staff-management": {
        "roles-access": shot("team/team-member-permissions"),
        "shift-planning": shot("calendar/time-off-list"),
        commissions: shot("team/commission-tiered"),
        performance: shot("reports/performance-dashboard"),
    },
    sales: {
        "daily-dashboard": shot("sales/sales-history"),
        "service-mix": shot("reports/report-sales"),
        "staff-sales": shot("team/earnings"),
        "exportable-reporting": shot("reports/reports-list"),
    },
    "multi-location": {
        "unified-account": shot("workspace-settings/locations-list"),
        "location-teams": shot("team/team-member-locations"),
        "location-taxes": shot("workspace-settings/location-overview"),
        "cross-location-reporting": shot("reports/performance-dashboard-filters"),
    },
    payroll: {
        "one-click-push": shot("team/payroll-gusto"),
        "time-tracking": shot("team/timesheets"),
        "tips-commissions": shot("team/earnings-expanded"),
        "taxes-deposits": shot("team/compensation-settings"),
    },
    "client-management": {
        "client-profiles": shot("clients/client-overview"),
        "consultation-history": shot("clients/client-new-note"),
        "waivers-intake": shot("clients/appointment-intake-forms"),
        "retention-notes": shot("reports/report-most-valuable-clients"),
    },
    "online-booking": {
        "booking-page": shot("online-presence/booking-site-services"),
        "direct-links": shot("online-presence/link-builder"),
        "social-booking": shot("online-presence/social-booking-setup"),
        "checkout-confidence": shot("online-presence/booking-site-details"),
    },
    memberships: {
        "recurring-plans": shot("gift-cards/packages-list"),
        "package-balances": shot("clients/client-wallet-balance"),
        "renewal-recovery": shot("reports/report-packages-activity"),
        "vip-retention": shot("clients/client-wallet-rewards"),
    },
    "inventory-retail-management": {
        "stock-visibility": shot("inventory/inventory-list"),
        "retail-checkout": shot("inventory/sell-product-select"),
        "reorder-timing": shot("inventory/inventory-product-edit"),
        "product-insights": shot("reports/report-retail-sales"),
    },
    payments: {
        "tap-to-pay": shot("payments/square-terminal-waiting"),
        "deposits-cards": shot("payments/collect-deposit"),
        "payout-speed": shot("payments/stripe-payouts-tab"),
        "safer-checkout": shot("payments/checkout-card-options"),
    },
    invoices: {
        "quick-creation": shot("sales/invoice-create"),
        "pay-links": shot("sales/invoice-add-items"),
        "status-tracking": shot("sales/invoices-list"),
        "deposit-recovery": shot("payments/deposit-summary"),
    },
    branding: {
        "branded-touchpoints": shot("online-presence/booking-site-professional"),
        "booking-polish": shot("online-presence/booking-site-time"),
        "gift-card-design": shot("gift-cards/gift-card-artwork"),
        "design-consistency": shot("marketing/marketing-templates"),
    },
    marketing: {
        "campaign-targeting": shot("marketing/campaign-client-filters"),
        "fill-open-time": shot("marketing/text-campaign-message"),
        "win-back": shot("marketing/email-campaign-message"),
        "automated-milestones": shot("marketing/automated-messages"),
    },
    "resource-management": {
        "rooms-spaces": shot("calendar/resources-list"),
        "shared-equipment": shot("calendar/resources-create"),
        "live-availability": shot("calendar/calendar-resources-view"),
    },
    "intake-forms": {
        "custom-forms": shot("clients/form-create"),
        "auto-send": shot("clients/form-enable"),
        "client-history": shot("clients/form-submissions"),
    },
    "soap-notes": {
        templates: shot("clients/soap-template-page"),
        "on-the-appointment": shot("clients/soap-note-annotate"),
        "sign-and-lock": shot("clients/soap-note-signed"),
    },
    "gift-cards": {
        "sell-online": shot("gift-cards/gift-card-online-sales"),
        "branded-delivery": shot("gift-cards/gift-card-send"),
        "easy-redemption": shot("gift-cards/gift-card-redeem"),
    },
    "pos-kiosk": {
        "kiosk-mode": `${KIOSK_SHOTS}kiosk-walkin.webp`,
        "fast-checkout": `${KIOSK_SHOTS}kiosk-select.webp`,
        "in-person-payments": `${KIOSK_SHOTS}kiosk-cart.webp`,
    },
    reports: {
        "performance-dashboard": shot("reports/performance-dashboard"),
        "sales-finance": shot("reports/report-sales"),
        "filters-exports": shot("reports/report-filters"),
        "report-access": shot("reports/report-access-roles"),
    },
    "data-transfer": {
        "client-import": shot("clients/client-import"),
        "service-import": shot("services/service-import"),
        "product-import": shot("inventory/inventory-import"),
    },
    notifications: {
        reminders: shot("calendar/automated-reminder-edit"),
        "appointment-updates": shot("calendar/automated-reschedule-edit"),
        waitlist: shot("calendar/automated-waitlist-edit"),
        "increase-bookings": shot("marketing/automated-messages"),
    },
    "email-marketing": {
        compose: shot("marketing/email-campaign-message"),
        audience: shot("marketing/email-campaign-clients"),
        templates: shot("marketing/marketing-templates"),
        "review-send": shot("marketing/email-campaign-review"),
    },
    "text-marketing": {
        "text-campaigns": shot("marketing/text-campaign-message"),
        targeting: shot("marketing/campaign-client-filters"),
        credits: shot("billing-and-fees/buy-credits-menu"),
        history: shot("marketing/messages-history"),
    },
};

// Existing landing photos used (blurred) behind each screenshot.
export const FEATURE_SHOWCASE_BACKGROUNDS = [
    "/images/landing_page/types/1.jpg",
    "/images/landing_page/types/2.jpg",
    "/images/landing_page/types/3.jpg",
    "/images/landing_page/types/4.jpg",
    "/images/landing_page/types/5.jpg",
    "/images/landing_page/types/6.jpg",
    "/images/landing_page/types/7.jpg",
    "/images/landing_page/types/8.jpg",
    "/images/landing_page/types/9.jpg",
    "/images/landing_page/types/10.jpg",
    "/images/landing_page/types/11.jpg",
    "/images/landing_page/spa-1.jpg",
    "/images/landing_page/spa-2.jpg",
    "/images/landing_page/spa-3.jpg",
    "/images/landing_page/spa-5.jpg",
];

// Soft warm gradient used instead of a photo on some visuals.
export const FEATURE_SHOWCASE_PLAIN_BACKGROUND =
    "var(--color-neutral-100, #f5f5f5)";

// Card position + background (clear photo, blurred photo or theme gradient)
// for each visual, Vagaro-style. Gradient visuals keep the card centered.
// Used in order down the page so neighbours never look the same.
export const FEATURE_SHOWCASE_LAYOUTS = [
    { position: "bottom-right", blur: true },
    { position: "center", plain: true },
    { position: "top-left", blur: true },
    { position: "center", blur: true },
    { position: "center", plain: true },
    { position: "top-right", blur: true },
    { position: "center", plain: true },
    { position: "bottom-right", blur: true },
    { position: "center", plain: true },
];

// Builds one row per tab with its screenshot and a background photo.
// The start offset changes per slug so pages don't all look the same.
export function getFeatureShowcaseRows(slug, tabs, feature) {
    const screenshots = FEATURE_SHOWCASE_SCREENSHOTS[slug] || {};
    const offset = [...(slug || "")].reduce((sum, ch) => sum + ch.charCodeAt(0), 0);

    return tabs.map((tab, index) => ({
        ...tab,
        screenshot: screenshots[tab.value] || feature?.main_image || null,
        background:
            FEATURE_SHOWCASE_BACKGROUNDS[(offset + index) % FEATURE_SHOWCASE_BACKGROUNDS.length],
    }));
}

// Screenshots shown per feature page below the hero.
export const MAX_SHOWCASE_IMAGES = 8;

// Full page order, Vagaro-style: the first two tab rows, then extra sections
// (single rows and two-card groups) mixed in with the remaining tab rows.
// Every visual gets its own background photo.
export function getFeatureShowcaseBlocks(slug, tabs, feature, more) {
    const tabRows = getFeatureShowcaseRows(slug, tabs, feature).map((row) => ({
        type: "row",
        ...row,
        href: more?.tabLinks?.[row.value] || null,
    }));
    // Keep every tab row, then add extra sections until the page has
    // MAX_SHOWCASE_IMAGES screenshots (a group counts as 2).
    let slots = MAX_SHOWCASE_IMAGES - tabRows.length;
    const extra = [];
    for (const section of more?.sections || []) {
        const cost = section.type === "group" ? section.cards.length : 1;
        if (cost <= slots) {
            extra.push(section);
            slots -= cost;
        }
    }

    const ordered = [...tabRows.slice(0, 2)];
    const restTabs = tabRows.slice(2);
    const longest = Math.max(restTabs.length, extra.length);
    for (let i = 0; i < longest; i++) {
        if (extra[i]) ordered.push(extra[i]);
        if (restTabs[i]) ordered.push(restTabs[i]);
    }

    const offset = [...(slug || "")].reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
    let visual = 0;
    // Background photo + card layout for the next visual on the page.
    const nextVisual = () => {
        const i = visual++;
        const layout = FEATURE_SHOWCASE_LAYOUTS[(offset + i) % FEATURE_SHOWCASE_LAYOUTS.length];
        return {
            background: FEATURE_SHOWCASE_BACKGROUNDS[(offset + i) % FEATURE_SHOWCASE_BACKGROUNDS.length],
            plainBackground: layout.plain ? FEATURE_SHOWCASE_PLAIN_BACKGROUND : null,
            layout,
        };
    };
    const toShot = (image) => (image ? (image.startsWith("/") ? image : shot(image)) : null);

    let rowIndex = 0;
    return ordered.map((block, index) => {
        if (block.type === "group") {
            return {
                ...block,
                key: `group-${index}`,
                cards: block.cards.map((card) => ({
                    ...card,
                    screenshot: toShot(card.image),
                    tablet: isTabletShot(card.image),
                    ...nextVisual(),
                })),
            };
        }
        const screenshot = block.screenshot || toShot(block.image);
        return {
            ...block,
            key: block.value || `row-${index}`,
            screenshot,
            tablet: isTabletShot(screenshot),
            ...nextVisual(),
            rowIndex: rowIndex++,
        };
    });
}
